import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { ReactNode, useMemo, useRef, useEffect } from 'react';

import {
  PORTAL_TERMS_REQUIRED_EVENT,
  TERMS_EXEMPT_MUTATIONS,
  usePortalTerms,
} from '../terms/context';
import { createGraphqlNetworkObservabilityLinks } from '../utilities/graphqlNetworkObservability';

type AuthorizedApolloProviderProps = {
  children: ReactNode | ReactNode[];
  clientId?: string;
  onMutationActivity?: (delta: number) => void;
  onAuthorizationError?: () => void;
};

const shouldRestartAuthFlow = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const authError = error as { error?: unknown; message?: unknown };

  return (
    authError.error === 'missing_refresh_token' ||
    authError.error === 'invalid_grant' ||
    (typeof authError.message === 'string' &&
      authError.message.startsWith('Missing Refresh Token'))
  );
};

const getCurrentRoute = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

export const AuthorizedApolloProvider = ({
  children,
  clientId,
  onMutationActivity,
  onAuthorizationError,
}: AuthorizedApolloProviderProps) => {
  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
  const terms = usePortalTerms();
  const termsRef = useRef(terms);
  termsRef.current = terms;
  const loginRedirectPromiseRef = useRef<Promise<void> | null>(null);

  const lifecycle = useMemo(() => ({ active: true }), [clientId, user?.sub]);
  useEffect(() => {
    lifecycle.active = true;
    return () => {
      lifecycle.active = false;
    };
  }, [lifecycle]);

  const apolloClient = useMemo(() => {
    const restartAuthFlow = () => {
      if (!loginRedirectPromiseRef.current) {
        loginRedirectPromiseRef.current = loginWithRedirect({
          appState: { returnTo: getCurrentRoute() },
        }).finally(() => {
          loginRedirectPromiseRef.current = null;
        });
      }

      return loginRedirectPromiseRef.current;
    };

    const getAccessTokenOrRestartAuth = async () => {
      try {
        return await getAccessTokenSilently();
      } catch (error) {
        if (lifecycle.active && shouldRestartAuthFlow(error)) {
          await restartAuthFlow();
        }

        throw error;
      }
    };

    const httpLink = createHttpLink({
      uri: import.meta.env.VITE_APP_GRAPHQL_API_URL,
      credentials: 'include',
    });

    const authLink = setContext(async (_, { headers }) => {
      const token = await getAccessTokenOrRestartAuth();
      if (!lifecycle.active) throw new Error('Client context changed');

      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : '',
          ...(clientId ? { 'x-ths-client-id': clientId } : {}),
        },
      };
    });

    // GraphQL response errors are reported here. Terminal transport failures
    // are reported by observabilityLink after any retries finish.
    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
      if (!lifecycle.active) return;
      if (
        graphQLErrors?.some(
          (error) => error.extensions?.code === 'PORTAL_TERMS_REQUIRED',
        )
      )
        window.dispatchEvent(new Event(PORTAL_TERMS_REQUIRED_EVENT));
      if (graphQLErrors) {
        graphQLErrors.forEach((error) => {
          console.error(
            `[GraphQL error]: Message: ${error.message}, Location: ${JSON.stringify(error.locations)}, Path: ${error.path}`,
          );

          // Create a readable error with full context
          const errorPath = error.path ? ` at ${error.path.join(' → ')}` : '';
          const readableError = new Error(
            `[${operation.operationName || 'GraphQL'}] ${error.message}${errorPath}`,
          );

          // Set error name for better grouping in Sentry
          readableError.name = 'GraphQLError';

          // Capture with Sentry with full context
          Sentry.captureException(readableError, {
            contexts: {
              graphql: {
                operationName: operation.operationName,
                operationType:
                  operation.query.definitions[0]?.kind || 'unknown',
                variables: operation.variables,
                query: operation.query.loc?.source.body,
                errorPath: error.path,
                errorLocations: error.locations,
                errorExtensions: error.extensions,
                errorMessage: error.message,
              },
            },
            tags: {
              errorType: 'graphql',
              operationName: operation.operationName || 'unknown',
              errorCode: String(error.extensions?.code || 'UNKNOWN'),
            },
            // Group similar errors together
            fingerprint: [
              'graphql',
              operation.operationName || 'unknown',
              error.message,
            ],
          });
        });
      }

      if (
        clientId &&
        (
          graphQLErrors ??
          (networkError &&
          'result' in networkError &&
          typeof networkError.result === 'object'
            ? networkError.result?.errors
            : []) ??
          []
        ).some(
          (error) =>
            error.extensions?.code === 'HOTEL_CLIENT_ACCESS_DENIED' ||
            error.extensions?.code === 'UNAUTHENTICATED',
        )
      )
        onAuthorizationError?.();
    });

    const { observabilityLink, attemptLink } =
      createGraphqlNetworkObservabilityLinks();

    const scopeFence = new ApolloLink(
      (operation, forward) =>
        new Observable((sink) => {
          if (!lifecycle.active) {
            sink.complete();
            return;
          }
          const definition = getMainDefinition(operation.query);
          const mutation =
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'mutation';
          if (
            mutation &&
            termsRef.current?.requiresAcceptance &&
            definition.kind === 'OperationDefinition' &&
            definition.selectionSet.selections.some(
              (selection) =>
                selection.kind !== 'Field' ||
                !TERMS_EXEMPT_MUTATIONS.has(selection.name.value),
            )
          ) {
            termsRef.current.review();
            sink.error(
              new Error('Accept the terms of service before making changes.'),
            );
            return;
          }
          // This submission is durably keyed and recovered on its booking route.
          // Leaving the company must not depend on its HTTP response arriving.
          const blocksNavigation =
            mutation && operation.operationName !== 'createAmadeusBooking';
          if (blocksNavigation) onMutationActivity?.(1);
          let finished = false;
          const finish = () => {
            if (!finished) {
              finished = true;
              if (blocksNavigation) onMutationActivity?.(-1);
            }
          };
          const subscription = forward(operation).subscribe({
            next: (result) => {
              if (lifecycle.active) sink.next(result);
            },
            error: (error) => {
              finish();
              if (lifecycle.active) sink.error(error);
            },
            complete: () => {
              finish();
              sink.complete();
            },
          });
          return () => {
            subscription.unsubscribe();
            finish();
          };
        }),
    );

    return new ApolloClient({
      devtools: { enabled: import.meta.env.MODE !== 'production' },
      defaultOptions: {
        query: {
          fetchPolicy: 'network-only',
        },
        watchQuery: {
          fetchPolicy: 'network-only',
        },
      },
      link: ApolloLink.from([
        scopeFence,
        observabilityLink,
        authLink,
        errorLink,
        attemptLink,
        httpLink,
      ]),
      cache: new InMemoryCache(),
    });
  }, [
    getAccessTokenSilently,
    loginWithRedirect,
    clientId,
    lifecycle,
    onMutationActivity,
    onAuthorizationError,
  ]);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};
